import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';

import { EditAssetModalDisplay } from './EditAssetModalDisplay';
import AssetService from '../../Services/AssetService';
import TemplateService from '../../Services/TemplateService';
import { hasWriteAccessToResource } from '../../RBAC/AccessTools';
import {
  PROVISIONING_MODEL_ID,
  XIRGO_PROVISIONING_MODEL_ID,
  XIRGO_V2_PROVISIONING_MODEL_ID,
} from '../../Constants/StaticUUIDs';
import {
  EPEC_6200_VENDOR_ID,
  ACTIA_ACU6_VENDOR_ID,
} from '../../Constants/Vendors';
import {
  removeFileOverridesByTag,
  tags,
  getParamsByFileType,
  file_types,
  getNetworkManagerWifiEnabled,
  getNetworkManagerWifiDisabled,
} from '../../Lib/FileOverrides';
import { ASSET_MODEL, REMOTE } from '../../Constants/RBAC';
import { hasReadAccessToResource } from '../../RBAC/AccessTools';

export default memo(EditAssetModal);

const initState = {
  availableTemplates: [],
  error: '',
  templatesLoading: true,
  updateRequestPending: false,
  viewing: 'basics',
};

function EditAssetModal(props) {
  const {
    asset: {
      _id: assetID,
      gtemplateID,
      nickname,
      orgID,
      remote,
      settings: currentSettings,
      vendor,
    } = {},
    isOpen,
    refreshAsset,
    updateAsset,
    toggle,
  } = props;
  const [state, updateState] = useState(() => {
    const asset = parseAssetProps(props);
    const commandAndControl = props.asset.remote ?? false;
    const links = parseAssetLinks(props);
    const selectedTimezone = props.asset.autoTz ? '' : props.asset.timezone;
    const timezone = props.asset.autoTz ? 'auto' : props.asset.timezone;
    return {
      ...initState,
      asset,
      commandAndControl,
      links,
      selectedTimezone,
      timezone,
      ...loadFileOverrides(props),
    };
  });

  const source = useMemo(() => CancelToken.source(), []);
  const assetService = useMemo(() => new AssetService(), []);
  const templateService = useMemo(() => new TemplateService(), []);

  const fetchAvailableAssetModels = useCallback(async () => {
    const limit = 100;
    const sort = 'name';
    const order = 'asc';
    let selectedTemplate;

    try {
      const { count, data } = await templateService.fetchPaginatedTemplates(
        1,
        orgID,
        limit,
        sort,
        order,
        undefined,
        vendor,
        source.token
      );
      const availableTemplates = transformTemplateData(data);
      if (count <= data.length) {
        if (gtemplateID) {
          selectedTemplate = availableTemplates.find(
            ({ value }) => value === gtemplateID
          );
        }
        return updateState((prevState) => ({
          ...prevState,
          availableTemplates,
          selectedTemplate,
          templatesLoading: false,
        }));
      }

      const additionalData =
        await templateService.handleAdditionalTemplateRequests({
          cancelToken: source.token,
          count,
          limit,
          order,
          orgID,
          sort,
          vendor,
        });

      const additionalTemplates = additionalData.map(({ data }) =>
        transformTemplateData(data)
      );
      const allTemplates = availableTemplates.concat(...additionalTemplates);
      if (gtemplateID) {
        selectedTemplate = allTemplates.find(
          ({ value }) => value === gtemplateID
        );
      }

      updateState((prevState) => ({
        ...prevState,
        availableTemplates: allTemplates,
        selectedTemplate,
        templatesLoading: false,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({ ...prevState, error }));
    }
  }, [gtemplateID, orgID, source.token, templateService, vendor]);

  const handleCCToggle = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      commandAndControl: !prevState.commandAndControl,
    }));
  }, []);

  const handlePropUpdate = useCallback(({ target: { name, value } }) => {
    updateState((prevState) => {
      const asset = { ...prevState.asset, [name]: value };
      return { ...prevState, asset, change: true, error: '' };
    });
  }, []);

  const handlePropUpdateSimple = useCallback(({ target: { name, value } }) => {
    updateState((prevState) => ({ ...prevState, [name]: value }));
  }, []);

  const handleTemplateSelect = useCallback((selectedTemplate) => {
    updateState((prevState) => ({ ...prevState, selectedTemplate }));
  }, []);

  const handleTimezoneSelected = useCallback((selectedTimezone) => {
    updateState((prevState) => ({
      ...prevState,
      selectedTimezone,
      timezone: selectedTimezone.value,
    }));
  }, []);

  const handleTimezoneToggle = useCallback((nextState) => {
    updateState((prevState) => {
      const timezone = nextState ? 'auto' : '';
      return {
        ...prevState,
        selectedTimezone: {},
        timezone,
      };
    });
  }, []);

  const submitUpdate = async (e) => {
    e.preventDefault();

    updateState((prevState) => ({
      ...prevState,
      updateRequestPending: true,
    }));
    try {
      validateAssetUpdates(state.asset, vendor);
      const { commandAndControl, links, selectedTemplate } = state;

      const autoTz = state.timezone === 'auto';
      const timezone = autoTz ? undefined : state.timezone;

      const settings = {
        ...getFileOverrideSettingsToSave(state, currentSettings || {}),
      };

      const asset = {
        ...state.asset,
        references: { links },
        timezone,
        autoTz,
        settings,
      };

      if (
        hasWriteAccessToResource(orgID, REMOTE) &&
        commandAndControl !== remote
      ) {
        await assetService.updateAssetRemote(
          assetID,
          commandAndControl,
          source.token
        );
      }

      const update = await assetService.updateAssetDetails(
        assetID,
        asset,
        source.token
      );

      if (selectedTemplate?.value && gtemplateID !== selectedTemplate.value) {
        await templateService.assignTemplate(
          assetID,
          selectedTemplate.value,
          {},
          source.token
        );
        return refreshAsset(false);
      }
      updateState((prevState) => ({
        ...prevState,
        asset: parseAssetProps({ asset: { ...update, autoTz, timezone } }),
        updateRequestPending: false,
      }));
      updateAsset(update);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        updateRequestPending: false,
      }));
    }
  };

  const toggleView = useCallback(
    (viewing) => {
      if (state.viewing === viewing) return;
      updateState((prevState) => ({ ...prevState, viewing }));
    },
    [state.viewing]
  );

  useEffect(() => {
    if (hasReadAccessToResource(orgID, ASSET_MODEL)) {
      fetchAvailableAssetModels();
    }

    return source.cancel;
  }, [fetchAvailableAssetModels, orgID, source.cancel]);

  return (
    <EditAssetModalDisplay
      {...state}
      currentTemplateID={gtemplateID}
      handleCCToggle={handleCCToggle}
      handlePropUpdate={handlePropUpdate}
      handlePropUpdateSimple={handlePropUpdateSimple}
      handleTemplateSelect={handleTemplateSelect}
      handleTimezoneSelected={handleTimezoneSelected}
      handleTimezoneToggle={handleTimezoneToggle}
      isOpen={isOpen}
      startingNickname={nickname}
      submitUpdate={submitUpdate}
      toggle={toggle}
      toggleView={toggleView}
      vendor={vendor}
    />
  );
}

EditAssetModal.propTypes = {
  asset: PropTypes.object,
  isOpen: PropTypes.bool,
  refreshAsset: PropTypes.func,
  toggle: PropTypes.func,
  updateAsset: PropTypes.func,
};

EditAssetModal.defaultProps = {
  asset: { nickname: '', references: {} },
};

function getFileOverrideSettingsToSave(state, existingSettings) {
  const { stwWiFiControl, stwWiFiEnabled, WiFissid, WiFipassword } = state;

  let file_overrides = removeFileOverridesByTag(
    existingSettings.file_overrides,
    tags.nm_wifi
  );

  if (!stwWiFiControl) return { file_overrides };

  if (stwWiFiEnabled) {
    file_overrides = file_overrides.concat(
      getNetworkManagerWifiEnabled({
        stwWiFiControl,
        stwWiFiEnabled,
        WiFissid,
        WiFipassword,
      })
    );
  } else {
    file_overrides = file_overrides.concat(getNetworkManagerWifiDisabled());
  }
  return { file_overrides };
}

function parseAssetProps({ asset }) {
  const {
    _id = '',
    mac = '',
    nickname = '',
    orgID = '',
    tags = '',
    vendor = '',
  } = asset;
  return { _id, mac, nickname, orgID, tags, vendor };
}

function parseAssetLinks({ asset }) {
  const { references = {} } = asset;
  return Array.isArray(references.links)
    ? references.links
    : [
        {
          name: '',
          url: '',
        },
        {
          name: '',
          url: '',
        },
        {
          name: '',
          url: '',
        },
      ];
}

function loadFileOverrides(props) {
  let output = {};

  const { file_overrides = [] } = props.asset?.settings || {}; // todo - change to gateway.settings and save/load stuff
  const stw_wifi = getParamsByFileType(
    file_overrides,
    file_types.wifi_nmconnection_file
  );
  if (stw_wifi === undefined || stw_wifi.stwWiFiControl === undefined) {
    output.stwWiFiControl = false;
    output.stwWiFiEnabled = false;
    output.WiFissid = '';
    output.WiFipassword = '';
  } else {
    output = { ...stw_wifi };
  }

  return output;
}

const provisionModelUuidSet = new Set([
  PROVISIONING_MODEL_ID,
  XIRGO_PROVISIONING_MODEL_ID,
  XIRGO_V2_PROVISIONING_MODEL_ID,
]);
function transformTemplateData(templates) {
  if (!Array.isArray(templates)) return [];
  return templates
    .filter((t) => !provisionModelUuidSet.has(t._id))
    .map(({ _id: value, name: label } = {}) => ({ label, value }));
}

function validateAssetUpdates({ nickname = '', mac = '' } = {}, vendor) {
  const serialNumberRegex = new RegExp(/^\d+$/); // will return false for empty string or if the string includes anything but integers
  switch (true) {
    case !nickname:
    case nickname?.includes('#'):
      throw new Error('Asset must have a name (# not allowed)');
    case !mac:
      throw new Error('Asset must be given a serial number');
    case !['1', EPEC_6200_VENDOR_ID, ACTIA_ACU6_VENDOR_ID].includes(vendor) &&
      !serialNumberRegex.test(mac):
      throw new Error('Serial number can only include digits 0-9');
    case vendor === EPEC_6200_VENDOR_ID && !mac.startsWith('epec-'):
      throw new Error(
        'epec serial numbers should start with "epec-" followed by the serial'
      );
    case vendor === ACTIA_ACU6_VENDOR_ID && !mac.startsWith('acu6-'):
      throw new Error(
        'acu6 serial numbers should start with "acu6-" followed by the serial'
      );
    case vendor === '1' && !isValidHex(mac):
      throw new Error('EID should be six hex bytes delimited by colons');
  }
}

function isValidHex(h) {
  if (typeof h !== 'string') return false;

  const hexBytesArr = h.split(':');
  if (!hexBytesArr.length) return false;

  return hexBytesArr.every((hb) => {
    const hex = Number.parseInt(hb, 16);
    return hex.toString(16).padStart(2, '0') === hb.toLowerCase();
  });
}
